import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight } from 'lucide-react';
import { api } from '../../services/api';
import { cn } from '../../utils/cn';
import { formatActivityAction, formatRelativeTime, formatAbsoluteTime } from '../../utils/activity';
import type { ActivityItem } from 'shared/types/index.js';

export function ActivityDropdown() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      api.getActivities({ limit: '5', scope: 'all' }).then(r => {
        if (r.success && r.data) setItems(r.data);
      });
    }
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 mr-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        title="最近の活動"
      >
        <Activity className="w-5 h-5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 animate-in">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-semibold dark:text-gray-200">最近の活動</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-400">活動はありません</div>
            ) : (
              items.map((item) => {
                const { icon, label, color } = formatActivityAction(item);
                return (
                  <div key={item.id} className="px-3 py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start gap-2">
                      <div className={cn('w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5', color)}>
                        {icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm dark:text-gray-200">
                          <span className="font-medium">{item.user_name}</span>
                          <span className="text-gray-500 dark:text-gray-400"> {label}</span>
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" title={formatAbsoluteTime(item.created_at)}>
                          {formatRelativeTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1 px-3 py-2 text-xs text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-t border-gray-100 dark:border-gray-700"
          >
            もっと見る <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
